import { motion } from "framer-motion";
import { Share2, QrCode, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/l6i8s5kw_WhatsApp%20Image%202569-03-26%20at%2010.38.24.jpeg";
const SITE_URL = "https://vet-impact-platform.preview.emergentagent.com";

const SOCIAL_LINKS = [
  { platform: "Twitter / X", handle: "@Bad_CompanyCo", url: "https://twitter.com/Bad_CompanyCo", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/x.svg" },
  { platform: "TikTok", handle: "@Bad.CompanyCo", url: "https://tiktok.com/@Bad.CompanyCo", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/tiktok.svg" },
  { platform: "Instagram", handle: "@badcompany_entertainment", url: "https://instagram.com/badcompany_entertainment", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/instagram.svg" },
  { platform: "Facebook", handle: "Bad Company Entertainment", url: "https://facebook.com/BadCompanyEntertainment", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/facebook.svg" },
  { platform: "YouTube", handle: "Bad Company Entertainment TV", url: "https://youtube.com/@BadCompanyEntertainmentTV", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/youtube.svg" },
  { platform: "Email", handle: "BadCompanyEntertainmentco@gmail.com", url: "mailto:BadCompanyEntertainmentco@gmail.com", icon: "https://cdn.jsdelivr.net/npm/simple-icons@v11/icons/gmail.svg" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

export default function SharePage() {
  const copyLink = () => {
    navigator.clipboard.writeText(SITE_URL);
    toast.success("Link copied to clipboard!");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Bad Company Entertainment",
          text: "Check out Bad Company - veteran-focused media and experiences from Las Vegas!",
          url: SITE_URL,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div data-testid="share-page">
      {/* Hero */}
      <section className="pt-32 pb-8 lg:pt-40 lg:pb-12 noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Spread The Word</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6"
          >
            Tell Others About<br/>Bad Company
          </motion.h1>
        </div>
      </section>

      {/* Find Us Everywhere - TOP */}
      <section className="py-16 lg:py-24 bg-zinc-950">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-3">Find Us Everywhere</h2>
            <p className="font-body text-sm text-zinc-500">All our official channels in one place</p>
          </div>
          <div className="flex justify-center mb-10">
            <img src={LOGO_URL} alt="Bad Company" className="h-20 w-20 object-contain" />
          </div>
          <div className="space-y-3">
            {SOCIAL_LINKS.map((s, i) => (
              <motion.a
                key={s.platform}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                data-testid={`social-link-${s.platform.toLowerCase().replace(/[\s\/]/g, '-')}`}
                className="flex items-center gap-5 border border-white/10 p-5 card-hover group"
              >
                <div className="w-12 h-12 border border-white/20 flex items-center justify-center shrink-0 group-hover:border-white/40 transition-colors">
                  <img
                    src={s.icon}
                    alt={s.platform}
                    className="w-6 h-6 invert opacity-50 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-subheading text-base uppercase tracking-wider text-zinc-300 group-hover:text-white transition-colors">{s.platform}</h3>
                  <p className="font-body text-sm text-zinc-500 truncate">{s.handle}</p>
                </div>
                <ExternalLink size={18} className="text-zinc-600 group-hover:text-white transition-colors shrink-0" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Share Actions */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-subheading text-2xl uppercase tracking-tight mb-3">Share Bad Company</h2>
            <p className="font-body text-sm text-zinc-500">Help us reach more veterans and supporters</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-16">
            <button
              onClick={nativeShare}
              data-testid="share-native-btn"
              className="border border-white/10 p-6 flex items-center gap-4 card-hover text-left"
            >
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center shrink-0">
                <Share2 size={20} className="text-zinc-400" />
              </div>
              <div>
                <h3 className="font-subheading text-sm uppercase tracking-wider">Share With Friends</h3>
                <p className="font-body text-xs text-zinc-500">Open share menu or copy link</p>
              </div>
            </button>
            <button
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="border border-white/10 p-6 flex items-center gap-4 card-hover text-left"
            >
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center shrink-0">
                <Copy size={20} className="text-zinc-400" />
              </div>
              <div>
                <h3 className="font-subheading text-sm uppercase tracking-wider">Copy Website Link</h3>
                <p className="font-body text-xs text-zinc-500">{SITE_URL.replace('https://', '')}</p>
              </div>
            </button>
          </div>

          {/* QR Code */}
          <div className="text-center">
            <div className="w-14 h-14 border border-white/20 flex items-center justify-center mx-auto mb-6">
              <QrCode size={24} className="text-zinc-400" />
            </div>
            <h3 className="font-subheading text-xl uppercase tracking-tight mb-4">QR Code</h3>
            <p className="font-body text-sm text-zinc-500 mb-6 max-w-md mx-auto">
              Share this QR code to make it easy for anyone to find Bad Company
            </p>
            <div className="border border-white/10 p-8 inline-block">
              <img src="https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/qzpax1n2_Find%20Us%20Everywhere%20Bad%20Company%20QR%20%283%29.jpg" alt="Bad Company QR Code" className="w-56 h-56 mx-auto" data-testid="website-qr-code" />
              <p className="font-body text-xs text-zinc-500 mt-3">Scan to find Bad Company everywhere</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
